import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  ApiClient, Heizungberechnung, HeizungPostenliste, HeizungPostenzeile, HeizungTeilstreckenzeile,
  HeizungVerbraucherliste, HeizungVerbraucherzeile, HeizungWegaufbau, HeizungWegliste, HeizungWegzeile2c,
} from '../api';
import {
  Baum, ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  formatiereZahl, spalteFuer, type BaumKnoten, type Fachstatus, type Spalte,
} from '../muster';
import { Schrankenmarke, istSchranke, schrankentext } from './heizung/schranke';
import {
  DRUCKGEFAELLE_TITEL, foerderhoeheText, grundtext, kpa, kw, zustandGuete, zustandHeizflaeche,
  zustandRechenherkunft,
} from './heizung/rohrnetz';

/**
 * Die Rohrnetzansicht – Gruppe → Klasse → Weg → Teilstrecke (TH-2 Stufe B nach TX-STANDARD S1).
 *
 * ═══ Warum ein Baum ════════════════════════════════════════════════════════
 *
 * Bei rund 880 Wegen mit zusammen über 76.000 Teilstreckenposten ist eine
 * flache Liste nicht benutzbar. Vier Ebenen, weil an einer Pumpe (Gruppe)
 * Verbraucherklassen mit ganz verschiedenem Druckbedarf hängen (Befund H-14),
 * darunter die Wege und ihre Teilstrecken. Die Ausnahme S1.3 (TX §2.6) ist mit
 * dem Baummuster aus TX3 erfüllt: WAI-ARIA-Baum mit Tastatur, Nachladen je
 * Knoten mit sichtbarer Anzahl, KEINE Kappung, die Blattebene als DataTable,
 * Zahlen mit Einheit im Kopf, Status als StatusBadge. Die flache Wegeliste gibt
 * es als Excel (Exportleiste der Seite) und je Klasse als CSV.
 *
 * ═══ Nachladen ═════════════════════════════════════════════════════════════
 *
 * Die Wege einer Gruppe kommen beim Aufklappen oder Wählen der Gruppe, die
 * Teilstrecken eines Wegs beim Aufklappen oder Wählen des Wegs, die Posten
 * beim Wählen der Teilstrecke. Alles im Voraus wären rund 39 MB.
 *
 * ═══ Was hier NICHT mehr steht ═════════════════════════════════════════════
 *
 * Ausdehnung und Absicherung je Gruppe stehen seit TH-2 auf der Seite
 * „Erzeugung und Absicherung“ – dieselbe Norm (DIN EN 12828), dieselben
 * Vorbehalte, eine Stelle.
 */

type Knotendaten =
  | { art: 'gruppe'; gruppe: string }
  | { art: 'klasse'; gruppe: string; klasse: string }
  | { art: 'weg'; gruppe: string; klasse: string; weg: HeizungWegzeile2c }
  | { art: 'teilstrecke'; gruppe: string; klasse: string; wegId: string; teilstrecke: HeizungTeilstreckenzeile };

type Netz = Heizungberechnung['netze'][number];

const klasseVon = (w: HeizungWegzeile2c) => w.verbraucherart ?? '(ohne)';

function Marke({ s }: { s: Fachstatus }) {
  return <StatusBadge klein zustand={s.zustand} text={s.text} />;
}

/** Ein Zustand je nachgeladenem Inhalt: laedt, Fehler oder Wert. */
type Ladung<T> = { laedt: true } | { fehler: unknown } | { wert: T };

export function Rohrnetzansicht({ api, projektId, daten }: {
  api: ApiClient;
  projektId: string;
  daten: Heizungberechnung;
}) {
  const gesamtzeilen = useMemo(() => daten.netze.filter((n) => n.verbraucherklasse == null), [daten.netze]);

  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [auswahl, setAuswahl] = useState<Knotendaten | null>(null);
  const [wege, setWege] = useState<Record<string, Ladung<HeizungWegliste>>>({});
  const [verbraucher, setVerbraucher] = useState<Record<string, Ladung<HeizungVerbraucherliste>>>({});
  const [aufbauten, setAufbauten] = useState<Record<string, Ladung<HeizungWegaufbau>>>({});
  const [posten, setPosten] = useState<Ladung<HeizungPostenliste> | null>(null);

  // Ein Abruf je Gruppe bzw. Weg, auch wenn Baum und Auswahl gleichzeitig fragen.
  const wegeAbrufe = useRef(new Map<string, Promise<HeizungWegliste>>());
  const aufbauAbrufe = useRef(new Map<string, Promise<HeizungWegaufbau>>());

  const wegeVon = useCallback((gruppe: string) => {
    let p = wegeAbrufe.current.get(gruppe);
    if (!p) {
      setWege((s) => ({ ...s, [gruppe]: { laedt: true } }));
      // Ohne Kappung: Die Grenze liegt über jeder Gruppe; meldet der Server
      // trotzdem `gekuerzt`, sagt die Gruppentafel das.
      p = api.heizungWege(projektId, { gruppe, grenze: 5000 });
      wegeAbrufe.current.set(gruppe, p);
      p.then((w) => setWege((s) => ({ ...s, [gruppe]: { wert: w } })))
        .catch((e) => { wegeAbrufe.current.delete(gruppe); setWege((s) => ({ ...s, [gruppe]: { fehler: e } })); });
    }
    return p;
  }, [api, projektId]);

  const verbraucherVon = useCallback((gruppe: string) => {
    if (verbraucher[gruppe] && !('fehler' in verbraucher[gruppe]!)) return;
    setVerbraucher((s) => ({ ...s, [gruppe]: { laedt: true } }));
    api.heizungVerbraucher(projektId, { gruppe, grenze: 5000 })
      .then((v) => setVerbraucher((s) => ({ ...s, [gruppe]: { wert: v } })))
      .catch((e) => setVerbraucher((s) => ({ ...s, [gruppe]: { fehler: e } })));
  }, [api, projektId, verbraucher]);

  const aufbauVon = useCallback((wegId: string) => {
    let p = aufbauAbrufe.current.get(wegId);
    if (!p) {
      setAufbauten((s) => ({ ...s, [wegId]: { laedt: true } }));
      p = api.heizungWegTeilstrecken(wegId);
      aufbauAbrufe.current.set(wegId, p);
      p.then((a) => setAufbauten((s) => ({ ...s, [wegId]: { wert: a } })))
        .catch((e) => { aufbauAbrufe.current.delete(wegId); setAufbauten((s) => ({ ...s, [wegId]: { fehler: e } })); });
    }
    return p;
  }, [api]);

  const postenLaden = useCallback((wegId: string, teilstreckeId: string | null) => {
    setPosten({ laedt: true });
    api.heizungWegPosten(wegId, { teilstrecke: teilstreckeId ?? 'ohne' })
      .then((l) => setPosten({ wert: l }))
      .catch((e) => setPosten({ fehler: e }));
  }, [api]);

  const wegknoten = (gruppe: string, klasse: string, w: HeizungWegzeile2c): BaumKnoten<Knotendaten> => ({
    id: `w:${w.id}`,
    titel: w.raumnummer ? `${w.raumnummer} · ${w.bezeichnung ?? '—'}` : (w.bezeichnung ?? '—'),
    hatKinder: w.hatRechenweg,
    daten: { art: 'weg', gruppe, klasse, weg: w },
    wert: `${formatiereZahl(kpa(w.druckverlustGesamtPa), 1)} kPa`,
    marke: w.grund
      ? <StatusBadge klein zustand="fehler" text={grundtext(w.grund)} />
      : !w.hatRechenweg ? <StatusBadge klein zustand="warnung" text="ohne Rechenweg" /> : undefined,
    hinweis: w.istMassgebend ? 'maßgebend' : undefined,
  });

  const knoten: BaumKnoten<Knotendaten>[] = gesamtzeilen.map((n) => ({
    id: `g:${n.systemgruppe}`,
    titel: n.systemgruppe,
    hatKinder: (n.wege ?? 0) > 0,
    anzahl: n.wege ?? undefined,
    daten: { art: 'gruppe', gruppe: n.systemgruppe },
    wert: `${foerderhoeheText(n.foerderhoehePa, n.vollstaendigkeit)} kPa`,
    marke: (n.verbraucherklassen ?? 0) > 1
      ? <StatusBadge klein zustand="warnung" text={`${n.verbraucherklassen} Verbraucherklassen`} />
      : undefined,
  }));

  const ladeKinder = async (k: BaumKnoten<Knotendaten>): Promise<BaumKnoten<Knotendaten>[]> => {
    const d = k.daten;
    if (d?.art === 'gruppe') {
      const liste = await wegeVon(d.gruppe);
      return liste.jeKlasse.map((kl) => ({
        id: `k:${d.gruppe}|${kl.klasse}`,
        titel: kl.klasse,
        anzahl: kl.wege,
        daten: { art: 'klasse', gruppe: d.gruppe, klasse: kl.klasse },
        wert: `${formatiereZahl(kpa(kl.hoechsterPa), 1)} kPa`,
        kinder: liste.zeilen.filter((w) => klasseVon(w) === kl.klasse).map((w) => wegknoten(d.gruppe, kl.klasse, w)),
      }));
    }
    if (d?.art === 'weg') {
      const a = await aufbauVon(d.weg.id);
      return a.teilstrecken.map((t) => ({
        id: `t:${d.weg.id}|${t.seite}|${t.nummer}`,
        titel: `${t.seite === 'vorlauf' ? 'VL' : 'RL'} ${t.kennzeichen ?? 'ohne Teilstrecke'}`,
        daten: { art: 'teilstrecke', gruppe: d.gruppe, klasse: d.klasse, wegId: d.weg.id, teilstrecke: t },
        wert: `${formatiereZahl(t.druckverlustPa, 0)} Pa`,
        marke: t.postenOhneBeiwert > 0
          ? <StatusBadge klein zustand="fehler" text={`${t.postenOhneBeiwert} ohne Beiwert`} /> : undefined,
      }));
    }
    return [];
  };

  const waehle = (id: string, d: Knotendaten) => {
    setGewaehlt(id);
    setAuswahl(d);
    if (d.art === 'gruppe') { void wegeVon(d.gruppe).catch(() => undefined); verbraucherVon(d.gruppe); }
    if (d.art === 'klasse') void wegeVon(d.gruppe).catch(() => undefined);
    if (d.art === 'weg') void aufbauVon(d.weg.id).catch(() => undefined);
    if (d.art === 'teilstrecke') postenLaden(d.wegId, d.teilstrecke.id);
  };

  return (
    <>
      <ContentSection titel="Wegebaum: Gruppe → Verbraucherklasse → Weg → Teilstrecke"
        beschreibung={'Pfeiltasten bewegen, → öffnet, ← schließt, Eingabe wählt. Die Anzahl am Knoten steht vor '
          + 'dem Laden fest; nichts wird gekappt. Unter dem Baum steht der gewählte Knoten.'}>
        <Baum<Knotendaten>
          name="Wegebaum Heizungsrohrnetz"
          knoten={knoten}
          ladeKinder={ladeKinder}
          gewaehlt={gewaehlt}
          beiWahl={(k) => { if (k.daten) waehle(k.id, k.daten); }}
          hoehe={420}
          leer={<EmptyState klein titel="Noch keine Auslegungsgruppe gerechnet."
            erklaerung="Ohne Rohrnetzlauf gibt es keine Gruppen, Wege und Teilstrecken." />}
        />
      </ContentSection>

      <ContentSection titel={auswahl ? titelDerAuswahl(auswahl) : 'Gewählter Knoten'}>
        {!auswahl && (
          <EmptyState klein titel="Noch nichts gewählt."
            erklaerung={'Im Baum eine Auslegungsgruppe wählen. Darunter die Verbraucherklassen, die Wege und ihre '
              + 'Teilstrecken – hier steht dann der Rechenweg, Posten für Posten, mit der Herkunft jeder Eingangsgröße.'} />
        )}
        {auswahl?.art === 'gruppe' && (
          <Gruppentafel
            netz={gesamtzeilen.find((n) => n.systemgruppe === auswahl.gruppe)!}
            klassenzeilen={daten.netze.filter((n) => n.verbraucherklasse != null && n.systemgruppe === auswahl.gruppe)}
            liste={wege[auswahl.gruppe] ?? { laedt: true }}
            verbraucher={verbraucher[auswahl.gruppe] ?? { laedt: true }}
            erneut={() => { wegeAbrufe.current.delete(auswahl.gruppe); void wegeVon(auswahl.gruppe).catch(() => undefined); verbraucherVon(auswahl.gruppe); }}
          />
        )}
        {auswahl?.art === 'klasse' && (
          <Klassentafel
            gruppe={auswahl.gruppe} klasse={auswahl.klasse}
            klassenzeile={daten.netze.find((n) => n.systemgruppe === auswahl.gruppe && n.verbraucherklasse === auswahl.klasse) ?? null}
            liste={wege[auswahl.gruppe] ?? { laedt: true }}
            waehleWeg={(w) => waehle(`w:${w.id}`, { art: 'weg', gruppe: auswahl.gruppe, klasse: auswahl.klasse, weg: w })}
          />
        )}
        {auswahl?.art === 'weg' && (
          <Wegtafel
            aufbau={aufbauten[auswahl.weg.id] ?? { laedt: true }}
            erneut={() => { aufbauAbrufe.current.delete(auswahl.weg.id); void aufbauVon(auswahl.weg.id).catch(() => undefined); }}
            waehleTeilstrecke={(t) => waehle(`t:${auswahl.weg.id}|${t.seite}|${t.nummer}`,
              { art: 'teilstrecke', gruppe: auswahl.gruppe, klasse: auswahl.klasse, wegId: auswahl.weg.id, teilstrecke: t })}
          />
        )}
        {auswahl?.art === 'teilstrecke' && (
          <Postentafel ladung={posten ?? { laedt: true }}
            erneut={() => postenLaden(auswahl.wegId, auswahl.teilstrecke.id)} />
        )}
      </ContentSection>
    </>
  );
}

function titelDerAuswahl(a: Knotendaten): string {
  switch (a.art) {
    case 'gruppe': return `Auslegungsgruppe ${a.gruppe}`;
    case 'klasse': return `Verbraucherklasse ${a.klasse} in ${a.gruppe}`;
    case 'weg': return `Weg ${a.weg.raumnummer ?? ''} ${a.weg.bezeichnung ?? ''}`.replace(/\s+/g, ' ').trim();
    default: return `Teilstrecke ${a.teilstrecke.kennzeichen ?? 'ohne Teilstreckenbezug'}`;
  }
}

/** Lade-, Fehler- und Wertzweig einer Ladung (S1.4). */
function Geladen<T>({ ladung, was, erneut, kinder }: {
  ladung: Ladung<T>; was: string; erneut?: () => void; kinder: (wert: T) => ReactNode;
}) {
  if ('laedt' in ladung) return <LoadingState text={`${was} werden geladen …`} />;
  if ('fehler' in ladung) {
    return <ErrorState klein meldung={`${was} konnten nicht geladen werden.`} technisch={ladung.fehler}
                       beiErneutVersuchen={erneut} />;
  }
  return <>{kinder(ladung.wert)}</>;
}

// ═══ Die Gruppe ═══════════════════════════════════════════════════════════

const KLASSEN: Spalte<Netz>[] = [
  { schluessel: 'klasse', titel: 'Klasse', immerSichtbar: true, wert: (k) => k.verbraucherklasse ?? '' },
  { schluessel: 'wege', titel: 'Wege mit Ergebnis', art: 'zahl', nachkomma: 0, wert: (k) => k.wegeMitErgebnis,
    inhalt: (k) => `${k.wegeMitErgebnis ?? 0} von ${k.wege ?? 0}` },
  { schluessel: 'leistung', titel: 'Leistung', ...spalteFuer('leistung_kw'), wert: (k) => kw(k.leistungW) },
  { schluessel: 'foerderhoehe', titel: 'Förderhöhe', art: 'zahl', einheit: 'kPa', nachkomma: 1,
    wert: (k) => kpa(k.foerderhoehePa), inhalt: (k) => foerderhoeheText(k.foerderhoehePa, k.vollstaendigkeit) },
  { schluessel: 'mittel', titel: 'Abgleich Mittel', ...spalteFuer('druck_kpa'), wert: (k) => kpa(k.abgleichMittelPa) },
  { schluessel: 'max', titel: 'Abgleich max.', ...spalteFuer('druck_kpa'), wert: (k) => kpa(k.abgleichMaxPa) },
  { schluessel: 'ventile', titel: 'Ventile unter Anschlag', art: 'zahl', nachkomma: 0, wert: (k) => k.ventileUnterAnschlag,
    inhalt: (k) => ((k.ventileGesamt ?? 0) > 0 ? `${k.ventileUnterAnschlag ?? 0} von ${k.ventileGesamt}` : '—') },
];

const VERBRAUCHER: Spalte<HeizungVerbraucherzeile>[] = [
  { schluessel: 'verbraucher', titel: 'Verbraucher', immerSichtbar: true, wert: (z) => z.bezeichnung ?? '',
    inhalt: (z) => (z.istMassgebend ? <strong>{z.bezeichnung ?? '—'} (maßgebend)</strong> : (z.bezeichnung ?? '—')) },
  { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raumnummer ?? '' },
  { schluessel: 'vs', titel: 'Volumenstrom', art: 'zahl', einheit: 'm³/h', nachkomma: 3, wert: (z) => z.volumenstromM3h },
  { schluessel: 'dp', titel: 'Δp Weg', ...spalteFuer('druck_kpa'), wert: (z) => kpa(z.druckverlustGesamtPa) },
  { schluessel: 'abgleich', titel: 'Abgleichdruck', ...spalteFuer('druck_kpa'), wert: (z) => kpa(z.abgleichPa) },
  { schluessel: 'stufe', titel: 'Voreinstellung', art: 'zahl', nachkomma: 1, wert: (z) => z.ventilVoreinstellung },
  { schluessel: 'kv', titel: 'kv', art: 'zahl', einheit: 'm³/h', nachkomma: 2, wert: (z) => z.ventilKvGewaehlt },
  { schluessel: 'autoritaet', titel: 'Ventilautorität', art: 'zahl', nachkomma: 2, wert: (z) => z.ventilAutoritaet },
  { schluessel: 'modell', titel: 'Modellwert', ...spalteFuer('leistung_w'), wert: (z) => z.modellleistungW, versteckt: true },
  { schluessel: 'norm', titel: 'Φ Norm', ...spalteFuer('leistung_w'), wert: (z) => z.normleistungW, versteckt: true },
  { schluessel: 'betrieb', titel: 'Φ Betrieb', ...spalteFuer('leistung_w'), wert: (z) => z.betriebsleistungW },
  { schluessel: 'ausM', titel: 'Φ aus ṁ', ...spalteFuer('leistung_w'), wert: (z) => z.leistungAusMassenstromW },
  { schluessel: 'heizlast', titel: 'Heizlast Raum', ...spalteFuer('leistung_w'), wert: (z) => z.heizlastRaumW },
  { schluessel: 'deckung', titel: 'Deckung', ...spalteFuer('anteil'), wert: (z) => z.deckungProzent },
  { schluessel: 'verhaeltnis', titel: 'Q/ṁ', art: 'zahl', nachkomma: 2, wert: (z) => z.verhaeltnis },
  { schluessel: 'bewertung', titel: 'Bewertung', art: 'status', wert: (z) => zustandHeizflaeche(z.bewertung).text,
    inhalt: (z) => <Marke s={zustandHeizflaeche(z.bewertung)} /> },
];

function Gruppentafel({ netz, klassenzeilen, liste, verbraucher, erneut }: {
  netz: Netz; klassenzeilen: Netz[]; liste: Ladung<HeizungWegliste>; verbraucher: Ladung<HeizungVerbraucherliste>;
  erneut: () => void;
}) {
  const summe = 'wert' in liste ? liste.wert.summe : null;
  return (
    <>
      <p>
        {netz.bezeichnung ?? netz.kreis ?? ''}{' '}
        <Schrankenmarke vollstaendigkeit={netz.vollstaendigkeit} wege={netz.wege} wegeMitErgebnis={netz.wegeMitErgebnis} />
      </p>
      {istSchranke(netz.vollstaendigkeit) && (
        <p className="hinweis warnung" role="note">{schrankentext(netz.wege, netz.wegeMitErgebnis)}</p>
      )}
      <KpiReihe>
        <KpiCard titel="Förderhöhe" wert={foerderhoeheText(netz.foerderhoehePa, netz.vollstaendigkeit)} einheit="kPa"
          vergleich={`${formatiereZahl(netz.foerderhoeheMWS, 2)} m WS${istSchranke(netz.vollstaendigkeit) ? ' (untere Schranke)' : ''}`} />
        <KpiCard titel="Wege mit Ergebnis" wert={`${netz.wegeMitErgebnis ?? 0} von ${netz.wege ?? 0}`} />
        <KpiCard titel="Vorlauf / Rücklauf" wert={`${formatiereZahl(netz.vorlaufC, 1)} / ${formatiereZahl(netz.ruecklaufC, 1)}`} einheit="°C" />
        <KpiCard titel="Volumenstrom" wert={netz.volumenstromM3h} einheit="m³/h" nachkomma={2} />
        <KpiCard titel="Leistung" wert={kw(netz.leistungW)} einheit="kW" nachkomma={1} />
        <KpiCard titel="Ventile unter Anschlag"
          wert={summe && summe.ventileGesamt > 0 ? `${summe.ventileUnterAnschlag} von ${summe.ventileGesamt}` : '—'}
          vergleich={`Dichte ${formatiereZahl(netz.dichteKgM3, 1)} kg/m³ · ν ${formatiereZahl(netz.viskositaetMm2S, 3)} mm²/s`
            + ((netz.glykolProzent ?? 0) > 0 ? ` · Glykol ${formatiereZahl(netz.glykolProzent, 0)} %` : '')} />
      </KpiReihe>
      {netz.hinweise && <p className="hinweis" role="note">{netz.hinweise}</p>}
      {'wert' in liste && liste.wert.gekuerzt && (
        <p className="hinweis warnung" role="note">
          Der Server hat die Wegeliste gekürzt: {liste.wert.zeilen.length} von {liste.wert.summe.wege} Wegen geladen.
        </p>
      )}

      {klassenzeilen.length > 0 && (
        <>
          <h3>Auswertung je Verbraucherklasse (Vergleichsrechnung)</h3>
          <p className="schwach">Dieselbe Kette je Klasse, als hinge jede an einer eigenen Pumpe – eine
            Vergleichsrechnung und kein Ergebnis; ob getrennt wird, entscheidet der Fachplaner.</p>
          <DataTable name="Verbraucherklassen" spalten={KLASSEN} zeilen={klassenzeilen} schluessel={(k) => k.id}
                     suche={false} dicht leer={<EmptyState klein titel="Keine Vergleichsrechnung." />} />
        </>
      )}

      <h3>Verbraucher der Gruppe</h3>
      <p className="schwach">Φ Betrieb ist die Normleistung nach DIN EN 442-2 auf die Auslegungstemperaturen
        umgerechnet; sie ist mit der Raumheizlast zu vergleichen, nicht die Normleistung.</p>
      <Geladen ladung={verbraucher} was="Die Verbraucher" erneut={erneut} kinder={(v) => (
        <DataTable name={`Verbraucher ${netz.systemgruppe}`} spalten={VERBRAUCHER} zeilen={v.zeilen}
                   schluessel={(z) => z.wegId} dicht ausfuhr={v.zeilen.length > 20}
                   ausfuhrDateiname={`heizung-verbraucher-${netz.systemgruppe}.csv`}
                   trefferText={`${v.summe.verbraucher} Verbraucher, davon ${v.summe.mitHeizflaeche} mit Heizflächenrechnung`}
                   leer={<EmptyState klein titel="Keine Verbraucher in dieser Gruppe." />} />
      )} />

      <p className="schwach">
        Ausdehnung, Druckhaltung und Absicherung dieser Gruppe stehen auf der Seite{' '}
        <Link to="../heizung-erzeugung">„Erzeugung und Absicherung“</Link>.
      </p>
    </>
  );
}

// ═══ Die Klasse ═══════════════════════════════════════════════════════════

const WEGE: Spalte<HeizungWegzeile2c>[] = [
  { schluessel: 'verbraucher', titel: 'Verbraucher', immerSichtbar: true, wert: (w) => w.bezeichnung ?? '',
    inhalt: (w) => (
      <>
        {w.istMassgebend ? <strong>{w.bezeichnung ?? '—'} (maßgebend)</strong> : (w.bezeichnung ?? '—')}
        {w.grund && <> <StatusBadge klein zustand="fehler" text={grundtext(w.grund)} /></>}
        {!w.grund && !w.hatRechenweg && <> <StatusBadge klein zustand="warnung" text="ohne Rechenweg" /></>}
      </>
    ) },
  { schluessel: 'raum', titel: 'Raum', wert: (w) => w.raumnummer ?? '' },
  { schluessel: 'quelle', titel: 'Rohr aus Gruppe', wert: (w) => w.systemgruppeQuelle ?? '', versteckt: true },
  { schluessel: 'mmodell', titel: 'ṁ / Modell', art: 'zahl', nachkomma: 2, wert: (w) => w.massenstromAbweichung },
  { schluessel: 'vs', titel: 'Volumenstrom', art: 'zahl', einheit: 'm³/h', nachkomma: 3, wert: (w) => w.volumenstromM3h },
  { schluessel: 'vl', titel: 'Δp Vorlauf', ...spalteFuer('druck_kpa'), wert: (w) => kpa(w.druckverlustVorlaufPa) },
  { schluessel: 'vb', titel: 'Δp Verbraucher', ...spalteFuer('druck_kpa'), wert: (w) => kpa(w.druckverlustVerbraucherPa) },
  { schluessel: 'rl', titel: 'Δp Rücklauf', ...spalteFuer('druck_kpa'), wert: (w) => kpa(w.druckverlustRuecklaufPa) },
  { schluessel: 'gesamt', titel: 'Δp gesamt', ...spalteFuer('druck_kpa'), wert: (w) => kpa(w.druckverlustGesamtPa),
    fuss: 'max' },
  { schluessel: 'abgleich', titel: 'Abgleichdruck', ...spalteFuer('druck_kpa'), wert: (w) => kpa(w.abgleichPa) },
  { schluessel: 'stufe', titel: 'Voreinstellung', art: 'zahl', nachkomma: 1, wert: (w) => w.ventilVoreinstellung },
  { schluessel: 'guete', titel: 'Güte', art: 'status', wert: (w) => zustandGuete(w.guete).text,
    inhalt: (w) => <Marke s={zustandGuete(w.guete)} /> },
];

function Klassentafel({ gruppe, klasse, klassenzeile, liste, waehleWeg }: {
  gruppe: string; klasse: string; klassenzeile: Netz | null; liste: Ladung<HeizungWegliste>;
  waehleWeg: (w: HeizungWegzeile2c) => void;
}) {
  return (
    <Geladen ladung={liste} was="Die Wege" kinder={(l) => {
      const zahlen = l.jeKlasse.find((k) => k.klasse === klasse);
      const wege = l.zeilen.filter((w) => klasseVon(w) === klasse);
      return (
        <>
          <KpiReihe>
            <KpiCard titel="Wege gerechnet" wert={`${zahlen?.mitErgebnis ?? 0} von ${zahlen?.wege ?? 0}`} />
            <KpiCard titel="Leistung" wert={kw(zahlen?.leistungW)} einheit="kW" nachkomma={1} />
            <KpiCard titel="höchster Druckverlust" wert={kpa(zahlen?.hoechsterPa)} einheit="kPa" nachkomma={1} />
            {klassenzeile && (
              <KpiCard titel="Förderhöhe dieser Klasse allein"
                wert={foerderhoeheText(klassenzeile.foerderhoehePa, klassenzeile.vollstaendigkeit)} einheit="kPa"
                vergleich="Vergleichsrechnung, kein Ergebnis" />
            )}
          </KpiReihe>
          <DataTable name={`Wege ${klasse} in ${gruppe}`} spalten={WEGE} zeilen={wege} schluessel={(w) => w.id}
                     dicht beiZeileKlick={waehleWeg} ausfuhr={wege.length > 20}
                     ausfuhrDateiname={`heizung-wege-${gruppe}-${klasse}.csv`}
                     leer={<EmptyState klein titel="Keine Wege in dieser Klasse." />} />
        </>
      );
    }} />
  );
}

// ═══ Der Weg ══════════════════════════════════════════════════════════════

/**
 * Die Teilstrecken eines Wegs. Der Fuß „Δp auf diesem Weg“ trägt den Wert des
 * Rechenkerns und zählt die Einbauteile ohne Beiwert als Fehlstellen: Dann ist
 * die Summe eine untere Schranke „≥“ (TX-STANDARD S1.3, Fehlstellenregel).
 */
function teilstreckenspalten(a: HeizungWegaufbau): Spalte<HeizungTeilstreckenzeile>[] {
  return [
  { schluessel: 'nr', titel: 'Nr', art: 'zahl', nachkomma: 0, immerSichtbar: true, wert: (t) => t.nummer },
  { schluessel: 'seite', titel: 'Seite', wert: (t) => (t.seite === 'vorlauf' ? 'Vorlauf' : 'Rücklauf') },
  { schluessel: 'ts', titel: 'Teilstrecke', wert: (t) => t.kennzeichen ?? 'ohne Teilstrecke',
    inhalt: (t) => (
      <>
        {t.kennzeichen ?? <StatusBadge klein zustand="warnung" text="ohne Teilstrecke" />}
        {t.teilstreckeIstMassgebend && <> <StatusBadge klein zustand="neutral" text="maßgebend" /></>}
      </>
    ) },
  { schluessel: 'posten', titel: 'Posten', art: 'zahl', nachkomma: 0, wert: (t) => t.posten, fuss: 'summe' },
  { schluessel: 'ohneBeiwert', titel: 'ohne Beiwert', art: 'zahl', nachkomma: 0, wert: (t) => t.postenOhneBeiwert, fuss: 'summe' },
  { schluessel: 'laenge', titel: 'Länge', ...spalteFuer('laenge_m'), wert: (t) => t.laengeM, fuss: { art: 'summe', schranke: true } },
  { schluessel: 'dn', titel: 'DN', wert: (t) => t.kleinsteNennweite ?? t.teilstreckeNennweite ?? '' },
  { schluessel: 'v', titel: 'v max.', ...spalteFuer('geschwindigkeit'), wert: (t) => t.maxGeschwindigkeitMs, fuss: 'max' },
  { schluessel: 'reibung', titel: 'Reibung', ...spalteFuer('druck_pa'), wert: (t) => t.reibungPa, fuss: { art: 'summe', schranke: true } },
  { schluessel: 'einzel', titel: 'Einzelwiderstände', ...spalteFuer('druck_pa'), wert: (t) => t.einzelPa,
    fuss: { art: 'summe', schranke: true } },
  { schluessel: 'dp', titel: 'Δp auf diesem Weg', ...spalteFuer('druck_pa'), wert: (t) => t.druckverlustPa,
    fuss: { fest: a.summe.druckverlustPa, fehlend: a.summe.postenOhneBeiwert, schranke: true } },
  { schluessel: 'summe', titel: 'Σ laufend', ...spalteFuer('druck_pa'), wert: (t) => t.summePa },
  { schluessel: 'ganz', titel: 'Δp ganze Teilstrecke', ...spalteFuer('druck_pa'), wert: (t) => t.teilstreckeDruckverlustPa },
  { schluessel: 'unterschied', titel: 'Unterschied', wert: (t) => t.unterschied ?? '', versteckt: true },
  { schluessel: 'herkunft', titel: 'Herkunft V̇', art: 'status', wert: (t) => zustandRechenherkunft(t.volumenstromHerkunft).text,
    inhalt: (t) => <Marke s={zustandRechenherkunft(t.volumenstromHerkunft)} /> },
  ];
}

function Wegtafel({ aufbau, erneut, waehleTeilstrecke }: {
  aufbau: Ladung<HeizungWegaufbau>; erneut: () => void; waehleTeilstrecke: (t: HeizungTeilstreckenzeile) => void;
}) {
  return (
    <Geladen ladung={aufbau} was="Die Teilstrecken" erneut={erneut} kinder={(a) => {
      let pruefungen: { pruefung: string; ergebnis: string; text: string }[] = [];
      try { if (a.gueteJson) pruefungen = JSON.parse(a.gueteJson); } catch { /* eine kaputte Zeile nimmt die Seite nicht mit */ }
      return (
        <>
          <KpiReihe>
            <KpiCard titel="Leistung" wert={kw(a.leistungW)} einheit="kW" nachkomma={1} />
            <KpiCard titel="Volumenstrom" wert={a.volumenstromM3h} einheit="m³/h" nachkomma={3}
              vergleich={`${formatiereZahl(a.massenstromKgH, 0)} kg/h · Spreizung ${formatiereZahl(a.spreizungK, 1)} K`} />
            <KpiCard titel="Δp Vorlauf / Verbraucher / Rücklauf"
              wert={`${formatiereZahl(kpa(a.druckverlustVorlaufPa), 1)} / ${formatiereZahl(kpa(a.druckverlustVerbraucherPa), 1)} / ${formatiereZahl(kpa(a.druckverlustRuecklaufPa), 1)}`}
              einheit="kPa" vergleich={a.verbraucherHerkunft ?? 'Verbraucher ohne Herkunft'} />
            <KpiCard titel="Δp gesamt" wert={kpa(a.druckverlustGesamtPa)} einheit="kPa" nachkomma={1}
              vergleich={a.abgleichPa === null ? undefined : `Abgleichdruck ${formatiereZahl(kpa(a.abgleichPa), 1)} kPa`} />
            {a.druckverlustKorrigiertPa !== null && (
              <KpiCard titel="mit vorgeschlagenen Nennweiten" wert={kpa(a.druckverlustKorrigiertPa)} einheit="kPa" nachkomma={1}
                vergleich={`${a.korrigierteRohre} Rohre über der Vorgabe`} />
            )}
          </KpiReihe>
          {pruefungen.length > 0 && (
            <ul className="liste">
              {pruefungen.map((p) => (
                <li key={p.pruefung}><Marke s={zustandGuete(p.ergebnis)} /> <strong>{p.pruefung}</strong> {p.text}</li>
              ))}
            </ul>
          )}
          {a.ventilHinweis && (
            <p className={a.ventilHinweis.includes('ANSCHLAG') ? 'hinweis fehler' : 'hinweis'} role="note">
              <strong>Ventil</strong> {a.ventilBaureihe ?? ''}
              {a.ventilKvErforderlich !== null && ` · erforderlich kv = ${formatiereZahl(a.ventilKvErforderlich, 3)} m³/h`}
              {a.ventilDruckPa !== null && ` · abzubauen ${formatiereZahl(kpa(a.ventilDruckPa), 1)} kPa`}
              {a.ventilVoreinstellung !== null && ` · Stufe ${formatiereZahl(a.ventilVoreinstellung, 1)}`}
              {' – '}{a.ventilHinweis}
            </p>
          )}
          {a.hinweis && <p className="hinweis" role="note">{a.hinweis}</p>}
          {a.grund && <EmptyState klein titel="Kein Rechenweg." erklaerung={a.grund} />}
          {a.teilstrecken.length > 0 && (
            <>
              <p className="schwach">Δp auf diesem Weg ist die Summe der Stücke, die der Weg durch die Teilstrecke führt;
                Δp der ganzen Teilstrecke steht daneben – ein abzweigender Weg trägt weniger. Ohne Beiwert fehlt ein
                Einzelwiderstand: Die Summe ist dann eine untere Schranke.</p>
              <DataTable name="Teilstrecken des Wegs" spalten={teilstreckenspalten(a)} zeilen={a.teilstrecken}
                         schluessel={(t) => `${t.seite}-${t.nummer}`} dicht beiZeileKlick={waehleTeilstrecke}
                         ausfuhr={a.teilstrecken.length > 20} ausfuhrDateiname="heizung-weg-teilstrecken.csv"
                         trefferText={`${a.summe.teilstrecken} Teilstrecken, ${a.summe.posten} Posten`
                           + (a.summe.ohneTeilstrecke > 0 ? `, ${a.summe.ohneTeilstrecke} Abschnitt(e) ohne Teilstreckenbezug` : '')}
                         leer={<EmptyState klein titel="Keine Teilstrecken." />} />
            </>
          )}
        </>
      );
    }} />
  );
}

// ═══ Die Teilstrecke – Posten für Posten ══════════════════════════════════

const POSTEN: Spalte<HeizungPostenzeile>[] = [
  { schluessel: 'nr', titel: 'Nr', art: 'zahl', nachkomma: 0, immerSichtbar: true, wert: (p) => p.nummer },
  { schluessel: 'art', titel: 'Art', wert: (p) => p.art },
  { schluessel: 'bauform', titel: 'Bauform', wert: (p) => p.bauform ?? (p.art === 'rohr' ? 'Rohr' : ''),
    inhalt: (p) => (
      <>
        {p.bauform ?? (p.art === 'rohr' ? 'Rohr' : '—')}
        {p.korrekturDn !== null && (
          <> <StatusBadge klein zustand="warnung" text={`Vorschlag DN ${p.korrekturDn}: ${formatiereZahl(p.korrekturPaM, 0)} statt ${formatiereZahl(p.druckgefaellePaM, 0)} Pa/m`} /></>
        )}
      </>
    ) },
  { schluessel: 'dn', titel: 'DN', wert: (p) => (p.dn === null ? '' : String(p.dn)) },
  { schluessel: 'laenge', titel: 'Länge', art: 'zahl', einheit: 'm', nachkomma: 3, wert: (p) => p.laengeM, fuss: 'summe' },
  { schluessel: 'm', titel: 'Massenstrom', ...spalteFuer('massenstrom'), wert: (p) => p.massenstromKgH },
  { schluessel: 'vs', titel: 'Volumenstrom', art: 'zahl', einheit: 'm³/h', nachkomma: 3, wert: (p) => p.volumenstromM3h },
  { schluessel: 'v', titel: 'v', ...spalteFuer('geschwindigkeit'), wert: (p) => p.geschwindigkeitMs, fuss: 'max' },
  { schluessel: 're', titel: 'Re', art: 'zahl', nachkomma: 0, wert: (p) => p.reynolds },
  { schluessel: 'lambda', titel: 'λ', art: 'zahl', nachkomma: 4, wert: (p) => p.lambda },
  { schluessel: 'zeta', titel: 'ζ', art: 'zahl', nachkomma: 2, wert: (p) => p.zeta },
  { schluessel: 'r', titel: 'R', art: 'zahl', einheit: 'Pa/m', nachkomma: 0, wert: (p) => p.druckgefaellePaM },
  // Eine Fehlstelle ist nie 0 Pa: Der Fuß zählt sie als „ohne Wert“ und die Summe wird zur Schranke.
  { schluessel: 'dp', titel: 'Δp', art: 'zahl', einheit: 'Pa', nachkomma: 2, wert: (p) => p.druckverlustPa,
    inhalt: (p) => (p.druckverlustPa === null ? <StatusBadge klein zustand="fehler" text="fehlt" /> : formatiereZahl(p.druckverlustPa, 2)),
    fuss: { art: 'summe', schranke: true } },
  { schluessel: 'summe', titel: 'Σ laufend', ...spalteFuer('druck_pa'), wert: (p) => p.summePa },
  { schluessel: 'rau', titel: 'Rauigkeit', art: 'status', wert: (p) => zustandRechenherkunft(p.rauigkeitHerkunft).text,
    inhalt: (p) => <Marke s={zustandRechenherkunft(p.rauigkeitHerkunft)} /> },
  { schluessel: 'beiwert', titel: 'Beiwert', art: 'status', wert: (p) => zustandRechenherkunft(p.beiwertHerkunft).text,
    inhalt: (p) => <Marke s={zustandRechenherkunft(p.beiwertHerkunft)} /> },
  { schluessel: 'begruendung', titel: 'Begründung Beiwert', wert: (p) => p.beiwertBegruendung ?? '', versteckt: true },
];

function Postentafel({ ladung, erneut }: { ladung: Ladung<HeizungPostenliste>; erneut: () => void }) {
  return (
    <Geladen ladung={ladung} was="Die Posten" erneut={erneut} kinder={(l) => (
      <>
        <p className="schwach">{l.weg}</p>
        <KpiReihe>
          <KpiCard titel="Posten" wert={l.summe.posten} vergleich={`${l.summe.rohre} Rohre`} />
          <KpiCard titel="Länge auf diesem Weg" wert={l.summe.laengeM} einheit="m" nachkomma={2}
            vergleich={l.teilstreckeLaengeM === null ? undefined : `ganze Teilstrecke ${formatiereZahl(l.teilstreckeLaengeM, 2)} m`} />
          <KpiCard titel="Reibung + Einzelwiderstände"
            wert={`${formatiereZahl(l.summe.reibungPa, 0)} + ${formatiereZahl(l.summe.einzelPa, 0)}`} einheit="Pa"
            vergleich={`zusammen ${formatiereZahl(l.summe.druckverlustPa, 0)} Pa`} />
          <KpiCard titel="höchste Geschwindigkeit" wert={l.summe.maxGeschwindigkeitMs} einheit="m/s" nachkomma={2} />
          {l.teilstreckeReynolds !== null && (
            <KpiCard titel="Re / λ der Teilstrecke"
              wert={`${formatiereZahl(l.teilstreckeReynolds, 0)} / ${formatiereZahl(l.teilstreckeLambda, 4)}`}
              vergleich={l.teilstreckeDruckgefaellePaM === null ? undefined
                : `${DRUCKGEFAELLE_TITEL}: ${formatiereZahl(l.teilstreckeDruckgefaellePaM, 0)} Pa/m`} />
          )}
        </KpiReihe>
        {l.summe.ohneBeiwert > 0 && (
          <p className="hinweis fehler" role="note">
            {l.summe.ohneBeiwert} Einbauteil(e) ohne Beiwert – ihr Einzelwiderstand FEHLT in der Summe. Der
            Druckverlust dieser Strecke ist eine <strong>untere Schranke</strong>.
          </p>
        )}
        {l.grund && <EmptyState klein titel="Keine Posten." erklaerung={l.grund} />}
        {l.posten.length > 0 && (
          <DataTable name="Posten der Teilstrecke" spalten={POSTEN} zeilen={l.posten}
                     schluessel={(p) => `${p.seite}-${p.nummer}`} dicht ausfuhr={l.posten.length > 20}
                     ausfuhrDateiname="heizung-teilstrecke-posten.csv"
                     leer={<EmptyState klein titel="Keine Posten." />} />
        )}
      </>
    )} />
  );
}
